import React from 'react';
import axios from 'axios';
import { maskUsPhone } from 'utilities/formatPhone';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { AutoComplete } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import '../../../styles/mastercertificate.css';
import '../../../styles/panels.css';

interface CreateNewCarriersProps {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

type RepRow = {
  firstName: string;
  middleInitial: string;
  lastName: string;
  email: string;
  phone: string;
  note: string;
};

type LineOfBusinessRow = {
  line: string;
};

type CustomFieldRow = {
  text: string;
  value: string;
};

type AgentCarrierCodeRow = {
  code: string;
  agent: string;
};

type SicItem = { code: string; description: string };

type CarrierLookupItem = {
  id: string;
  name: string;
  naicCode: string | null;
  isExisting: boolean;
};

const CreateNewCarriers: React.FC<CreateNewCarriersProps> = ({ open, onClose, onSaved }) => {
  const [searchCarrier, setSearchCarrier] = React.useState<CarrierLookupItem | string | undefined>(undefined);
  const [searchSuggestions, setSearchSuggestions] = React.useState<CarrierLookupItem[]>([]);
  const searchDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Google Maps address autocomplete
  const [addressSuggestions, setAddressSuggestions] = React.useState<any[]>([]);
  const [addressLoading, setAddressLoading] = React.useState(false);
  const address1Ref = React.useRef<HTMLInputElement>(null);
  const addressDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    if (!open) return;
    if (!window.google || !window.google.maps) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }, [open]);

  const handleAddress1Change = React.useCallback((value: string) => {
    setAddress1(value);
    if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);
    if (value.length > 2 && window.google && window.google.maps && window.google.maps.places) {
      setAddressLoading(true);
      addressDebounceRef.current = setTimeout(() => {
        const autocompleteService = new window.google.maps.places.AutocompleteService();
        autocompleteService.getPlacePredictions(
          { input: value, types: ['address'] },
          (predictions: any[]) => {
            setAddressSuggestions(predictions || []);
            setAddressLoading(false);
          }
        );
      }, 300);
    } else {
      setAddressSuggestions([]);
      setAddressLoading(false);
    }
  }, []);

  const selectAddressSuggestion = React.useCallback((prediction: any) => {
    // Use the street-only part of the prediction as immediate value
    const streetText = prediction.structured_formatting?.main_text || prediction.description.split(',')[0].trim();
    setAddress1(streetText);
    setAddressSuggestions([]);
    setAddressLoading(false);
    if (window.google && window.google.maps && window.google.maps.places) {
      const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
      placesService.getDetails({ placeId: prediction.place_id, fields: ['address_components', 'formatted_address', 'name'] }, (place: any, status: any) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          let streetNumber = '', route = '';
          let placeCityLocality = '', placeCitySublocality = '', placeCityAdmin3 = '';
          let placeState = '', placeZip = '', placeCounty = '';

          if (place.address_components) {
            place.address_components.forEach((component: any) => {
              const types = component.types;
              if (types.includes('street_number'))         streetNumber        = component.long_name;
              if (types.includes('route'))                 route               = component.long_name;
              if (types.includes('locality'))              placeCityLocality   = component.long_name;
              if (types.includes('sublocality_level_1') || types.includes('sublocality'))
                                                           placeCitySublocality = component.long_name;
              if (types.includes('administrative_area_level_3')) placeCityAdmin3 = component.long_name;
              if (types.includes('administrative_area_level_2')) placeCounty   = component.long_name;
              if (types.includes('administrative_area_level_1')) placeState    = component.short_name;
              if (types.includes('postal_code'))           placeZip            = component.long_name;
            });
          }

          // Street: prefer number+route, fall back to prediction main text
          const street = `${streetNumber} ${route}`.trim();
          setAddress1(street || streetText);

          // City: locality > sublocality (boroughs like Brooklyn) > admin level 3
          const placeCity = placeCityLocality || placeCitySublocality || placeCityAdmin3;
          if (placeCity)   setCity(placeCity);
          if (placeCounty) setCounty(placeCounty);
          if (placeState)  setStateValue(placeState);
          if (placeZip)    setZip(placeZip);
        }
      });
    }
  }, []);

  // Reset form state whenever the dialog is closed
  React.useEffect(() => {
    if (!open) {
      setSearchCarrier(undefined);
      setSearchSuggestions([]);
      setName('');
      setNaic('');
      setNaicCode('');
      setSaveError(null);
    }
  }, [open]);

  // SIC list
  const [sicList, setSicList] = React.useState<SicItem[]>([]);
  const [sicSuggestions, setSicSuggestions] = React.useState<SicItem[]>([]);
  React.useEffect(() => {
    if (!open) return;
    const ncToken = localStorage.getItem('NCToken') || '';
    const igToken = localStorage.getItem('igtoken') || '';
    fetch(`${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/sic-list`, {
      headers: { Authorization: `Bearer ${igToken}`, NCToken: ncToken, 'Content-Type': 'application/json' },
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then((data: any) => {
        const raw: any[] = Array.isArray(data) ? data : (Array.isArray(data?.value) ? data.value : []);
        setSicList(raw.map(item => ({
          code: String(item.sicCode ?? item.code ?? item.SicCode ?? item.Code ?? ''),
          description: String(item.sicDescription ?? item.description ?? item.SicDescription ?? item.Description ?? ''),
        })));
      })
      .catch(() => setSicList([]));
  }, [open]);

  // General
  const [name, setName] = React.useState('');
  const [fein, setFein] = React.useState('');
  const [sicCode, setSicCode] = React.useState('');
  const [sic, setSic] = React.useState('');
  const [yearStarted, setYearStarted] = React.useState('');
  const [naicCode, setNaicCode] = React.useState('');
  const [naic, setNaic] = React.useState('');
  const [carrierId, setCarrierId] = React.useState('');
  const [active, setActive] = React.useState(true);

  const [address1, setAddress1] = React.useState('');
  const [address2, setAddress2] = React.useState('');
  const [city, setCity] = React.useState('');
  const [stateValue, setStateValue] = React.useState('');
  const [county, setCounty] = React.useState('');
  const [zip, setZip] = React.useState('');
  const [financeCoSearchCode, setFinanceCoSearchCode] = React.useState('');
  const [leadSource, setLeadSource] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [agency, setAgency] = React.useState('');
  const [primaryLocation, setPrimaryLocation] = React.useState('');

  const [reps, setReps] = React.useState<RepRow[]>([{
    firstName: '',
    middleInitial: '',
    lastName: '',
    email: '',
    phone: '',
    note: '',
  }]);

  const [contactPhone, setContactPhone] = React.useState('');
  const [contactCellPhone, setContactCellPhone] = React.useState('');
  const [smsPhone, setSmsPhone] = React.useState('');
  const [fax, setFax] = React.useState('');
  const [primaryEmail, setPrimaryEmail] = React.useState('');
  const [secondaryEmail, setSecondaryEmail] = React.useState('');
  const [parent, setParent] = React.useState('');
  const [otherEmail, setOtherEmail] = React.useState('');
  const [mainWebsite, setMainWebsite] = React.useState('');
  const [agentPortal, setAgentPortal] = React.useState('');
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [agencyId, setAgencyId] = React.useState('');

  const [remitBy, setRemitBy] = React.useState('');
  const [grossCommissionDueWithin, setGrossCommissionDueWithin] = React.useState('');
  const [remainingPayments, setRemainingPayments] = React.useState('');
  const [minimumDownPayment, setMinimumDownPayment] = React.useState('');
  const [minimumDownPaymentUnit, setMinimumDownPaymentUnit] = React.useState<'%' | 'amount'>('%');
  const [interestRate, setInterestRate] = React.useState('');
  const [interestRateType, setInterestRateType] = React.useState('compound');
  const [minimumEarned, setMinimumEarned] = React.useState('');
  const [minimumEarnedUnit, setMinimumEarnedUnit] = React.useState<'%' | 'amount'>('%');
  const [amBestBcr, setAmBestBcr] = React.useState('');
  const [amBestFsc, setAmBestFsc] = React.useState('');
  const [premiumSent, setPremiumSent] = React.useState('');

  const [linesOfBusiness, setLinesOfBusiness] = React.useState<LineOfBusinessRow[]>([
    { line: '' },
  ]);

  const [customFields, setCustomFields] = React.useState<CustomFieldRow[]>([
    { text: '', value: '' },
  ]);

  const [agentCarrierCodes, setAgentCarrierCodes] = React.useState<AgentCarrierCodeRow[]>([
    { code: '', agent: '' },
  ]);

  const stateOptions = React.useMemo(
    () => [
      { label: 'Alabama', value: 'AL' },
      { label: 'Alaska', value: 'AK' },
      { label: 'Arizona', value: 'AZ' },
      { label: 'Arkansas', value: 'AR' },
      { label: 'California', value: 'CA' },
      { label: 'Colorado', value: 'CO' },
      { label: 'Connecticut', value: 'CT' },
      { label: 'Delaware', value: 'DE' },
      { label: 'District of Columbia', value: 'DC' },
      { label: 'Florida', value: 'FL' },
      { label: 'Georgia', value: 'GA' },
      { label: 'Hawaii', value: 'HI' },
      { label: 'Idaho', value: 'ID' },
      { label: 'Illinois', value: 'IL' },
      { label: 'Indiana', value: 'IN' },
      { label: 'Iowa', value: 'IA' },
      { label: 'Kansas', value: 'KS' },
      { label: 'Kentucky', value: 'KY' },
      { label: 'Louisiana', value: 'LA' },
      { label: 'Maine', value: 'ME' },
      { label: 'Maryland', value: 'MD' },
      { label: 'Massachusetts', value: 'MA' },
      { label: 'Michigan', value: 'MI' },
      { label: 'Minnesota', value: 'MN' },
      { label: 'Mississippi', value: 'MS' },
      { label: 'Missouri', value: 'MO' },
      { label: 'Montana', value: 'MT' },
      { label: 'Nebraska', value: 'NE' },
      { label: 'Nevada', value: 'NV' },
      { label: 'New Hampshire', value: 'NH' },
      { label: 'New Jersey', value: 'NJ' },
      { label: 'New Mexico', value: 'NM' },
      { label: 'New York', value: 'NY' },
      { label: 'North Carolina', value: 'NC' },
      { label: 'North Dakota', value: 'ND' },
      { label: 'Ohio', value: 'OH' },
      { label: 'Oklahoma', value: 'OK' },
      { label: 'Oregon', value: 'OR' },
      { label: 'Pennsylvania', value: 'PA' },
      { label: 'Rhode Island', value: 'RI' },
      { label: 'South Carolina', value: 'SC' },
      { label: 'South Dakota', value: 'SD' },
      { label: 'Tennessee', value: 'TN' },
      { label: 'Texas', value: 'TX' },
      { label: 'Utah', value: 'UT' },
      { label: 'Vermont', value: 'VT' },
      { label: 'Virginia', value: 'VA' },
      { label: 'Washington', value: 'WA' },
      { label: 'West Virginia', value: 'WV' },
      { label: 'Wisconsin', value: 'WI' },
      { label: 'Wyoming', value: 'WY' },
    ],
    [],
  );

  const yesNoOptions = React.useMemo(
    () => [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
    [],
  );

  const percentAmountOptions = React.useMemo(
    () => [
      { label: '%', value: '%' },
      { label: 'Amount', value: 'amount' },
    ],
    [],
  );

  const interestTypeOptions = React.useMemo(
    () => [
      { label: 'Compound', value: 'compound' },
      { label: 'Simple', value: 'simple' },
    ],
    [],
  );

  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const handleSave = async () => {
    if (!name.trim()) {
      setSaveError('Carrier name is required.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const payload: Record<string, unknown> = {
        name: name.trim(),
        fein: fein || null,
        sicCode: sicCode || null,
        sicDescription: sic || null,
        yearBusinessStarted: yearStarted ? parseInt(yearStarted, 10) : null,
        naicCode: naicCode || null,
        active,
        addressLine1: address1 || null,
        addressLine2: address2 || null,
        city: city || null,
        state: stateValue || null,
        county: county || null,
        zipCode: zip || null,
        searchCode: financeCoSearchCode || null,
        description: description || null,
        phone: contactPhone || null,
        cellPhone: contactCellPhone || null,
        smsPhone: smsPhone || null,
        fax: fax || null,
        eMail: primaryEmail || null,
        eMail2: secondaryEmail || null,
        eMail3: otherEmail || null,
        website: mainWebsite || null,
        agentPortal: agentPortal || null,
        paymentRemitBy: remitBy ? parseInt(remitBy, 10) : null,
        grossCommissionDueWithin: grossCommissionDueWithin ? parseInt(grossCommissionDueWithin, 10) : null,
        remainingPayments: remainingPayments ? parseInt(remainingPayments, 10) : null,
        minimumDownPaymentValue: minimumDownPayment ? parseFloat(minimumDownPayment) : null,
        minimumDownPaymentType: minimumDownPaymentUnit === '%' ? 1 : 2,
        interestRateValue: interestRate ? parseFloat(interestRate) : null,
        interestRateType: interestRateType === 'compound' ? 1 : 2,
        minimumEarnedValue: minimumEarned ? parseFloat(minimumEarned) : null,
        minimumEarnedType: minimumEarnedUnit === '%' ? 1 : 2,
      };

      await axios.post(
        `${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/carrier/insert`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('igtoken')}`,
            'Content-Type': 'application/json',
            NCToken: localStorage.getItem('NCToken') || '',
          },
        }
      );

      onSaved?.();
      onClose();
    } catch {
      setSaveError('Failed to save carrier. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSearchComplete = (e: { query: string }) => {
    const query = e.query || '';
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (!query.trim()) {
      setSearchSuggestions([]);
      return;
    }
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const ncToken = localStorage.getItem('NCToken') || '';
        const igToken = localStorage.getItem('igtoken') || '';
        const res = await fetch(
          `${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/carrier-lookup?search=${encodeURIComponent(query)}&top=50`,
          { headers: { Authorization: `Bearer ${igToken}`, NCToken: ncToken } }
        );
        if (!res.ok) { setSearchSuggestions([]); return; }
        const data = await res.json();
        const items: CarrierLookupItem[] = Array.isArray(data?.value) ? data.value : [];
        setSearchSuggestions(items.filter(i => i.name?.trim()));
      } catch {
        setSearchSuggestions([]);
      }
    }, 300);
  };

  const handleCarrierSelect = (item: CarrierLookupItem) => {
    setName(item.name?.trim() ?? '');
    setNaic(item.naicCode ?? '');
    setNaicCode(item.naicCode ?? '');
  };

  const addRepRow = () => {
    setReps((prev) => [
      ...prev,
      { firstName: '', middleInitial: '', lastName: '', email: '', phone: '', note: '' },
    ]);
  };

  const updateRepRow = (index: number, field: keyof RepRow, value: string) => {
    setReps((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const removeRepRow = (index: number) => {
    setReps((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const addLineOfBusinessRow = () => {
    setLinesOfBusiness((prev) => [...prev, { line: '' }]);
  };

  const updateLineOfBusinessRow = (index: number, value: string) => {
    setLinesOfBusiness((prev) => {
      const next = [...prev];
      next[index] = { line: value };
      return next;
    });
  };

  const removeLineOfBusinessRow = (index: number) => {
    setLinesOfBusiness((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const addCustomFieldRow = () => {
    setCustomFields((prev) => [...prev, { text: '', value: '' }]);
  };

  const updateCustomFieldRow = (index: number, field: keyof CustomFieldRow, value: string) => {
    setCustomFields((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addAgentCarrierCodeRow = () => {
    setAgentCarrierCodes((prev) => [...prev, { code: '', agent: '' }]);
  };

  const updateAgentCarrierCodeRow = (index: number, field: keyof AgentCarrierCodeRow, value: string) => {
    setAgentCarrierCodes((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const removeAgentCarrierCodeRow = (index: number) => {
    setAgentCarrierCodes((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  if (!open) return null;

  return (
    <div className="masterCert__popupOverlay">
      <div className="masterCert__popup">
        <div className="masterCert__popupHeader">
          <span>Add New Carrier</span>
          <button className="masterCert__popupCloseBtn" onClick={onClose}>×</button>
        </div>
        <div className="carrierDialog__body">
          {/* Search Existing Carriers (match AddAdditionalInterestDialog styling) */}
          <div className="carrierDialog__card">
            <div className="carrierDialog__label">
              Search List of Existing Carriers:
            </div>
            <div className="masterCert__dateCell">
            <AutoComplete
              value={searchCarrier}
              suggestions={searchSuggestions}
              completeMethod={handleSearchComplete}
              field="name"
              itemTemplate={(item: CarrierLookupItem) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: 13, color: '#1f2937' }}>{item.name}</span>
                  {item.naicCode && (
                    <span style={{ fontSize: 11, color: '#6b7280' }}>NAIC: {item.naicCode}</span>
                  )}
                </div>
              )}
              onChange={(e) => setSearchCarrier(e.value)}
              onSelect={(e) => handleCarrierSelect(e.value)}
              placeholder="Search carriers"
              dropdown
              style={{ width: 300 }}
              inputStyle={{ width: 300 }}
            />
            </div>
          </div>

          {/* General */}
          <div className="carrierDialog__card">
            <div className="carrierDialog__sectionTitle">General</div>
            <div className="carrierDialog__twoCol">
              <div>
                <div className="carrierDialog__label">
                  Name <span className="carrierDialog__required">*</span>
                </div>
                <InputText maxLength={100}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Please enter name"
                  className="carrierDialog__input"
                />

                {false && (<>
                <div className="carrierDialog__label">FEIN</div>
                <InputText
                  value={fein}
                  onChange={(e) => setFein(e.target.value)}
                  placeholder="Please enter FEIN"
                  className="carrierDialog__input"
                />
                </>)}

                {false && (<>
                <div className="carrierDialog__label">SIC</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                  <InputText
                    value={sicCode}
                    onChange={(e) => setSicCode(e.target.value)}
                    placeholder="Code"
                    className="carrierDialog__input"
                    style={{ width: 80, flexShrink: 0, margin: 0 }}
                  />
                  <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                    <AutoComplete
                      value={sic}
                      suggestions={sicSuggestions}
                      completeMethod={(e) => {
                        const q = e.query.toLowerCase();
                        setSicSuggestions(
                          sicList.filter(s =>
                            s.code.toLowerCase().includes(q) ||
                            s.description.toLowerCase().includes(q)
                          ).slice(0, 50)
                        );
                      }}
                      field="description"
                      itemTemplate={(item: SicItem) => (
                        <div style={{ display: 'flex', gap: 8, fontSize: 12 }}>
                          <span style={{ color: '#A200FF', fontWeight: 600, minWidth: 40 }}>{item.code}</span>
                          <span style={{ color: '#374151' }}>{item.description}</span>
                        </div>
                      )}
                      onChange={(e) => {
                        if (typeof e.value === 'string') setSic(e.value);
                      }}
                      onSelect={(e) => {
                        const item: SicItem = e.value;
                        setSic(item.description);
                        setSicCode(item.code);
                      }}
                      placeholder="Search SIC description"
                      inputClassName="carrierDialog__input"
                      inputStyle={{ width: '100%', paddingRight: 30, margin: 0, boxSizing: 'border-box' }}
                      style={{ display: 'block', width: '100%' }}
                      pt={{ root: { style: { display: 'block', width: '100%' } } }}
                    />
                    <i
                      className="pi pi-chevron-down"
                      style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#6b7280', pointerEvents: 'none', zIndex: 1 }}
                    />
                  </div>
                </div>
                </>)}
              </div>

              <div>
                {false && (<>
                <div className="carrierDialog__label">Year Business Started</div>
                <InputText
                  value={yearStarted}
                  onChange={(e) => setYearStarted(e.target.value)}
                  placeholder="Please enter year"
                  className="carrierDialog__input"
                />
                </>)}

                {false && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div className="carrierDialog__label">NAIC Code</div>
                    <InputText
                      value={naicCode}
                      onChange={(e) => setNaicCode(e.target.value)}
                      placeholder="Please enter NAIC code"
                      className="carrierDialog__input"
                    />
                  </div>
                </div>
                )}

                   <div style={{ flex: 1 }}>
                    <div className="carrierDialog__label">NAIC</div>
                    <InputText maxLength={30}
                      value={naic}
                      onChange={(e) => setNaic(e.target.value)}
                      placeholder="Please enter NAIC"
                      className="carrierDialog__input"
                    />
                  </div>

                {false && (
                <div style={{ flex: 1 }}>
                  <div className="carrierDialog__label">ID</div>
                  <InputText
                    value={carrierId}
                    onChange={(e) => setCarrierId(e.target.value)}
                    placeholder="Please enter ID"
                    className="carrierDialog__input"
                    style={{ margin: 0 }}
                  />
                </div>
                )}
                {false && (
                <div className="carrierDialog__checkboxWrapper" style={{ paddingTop: 8 }}>
                  <Checkbox
                    inputId="carrierActive"
                    checked={active}
                    onChange={(e) => setActive(!!e.checked)}
                  />
                  <label htmlFor="carrierActive" className="carrierDialog__checkboxLabel">
                    Active
                  </label>
                </div>
                )}
              </div>
            </div>
          </div>

          {/* Address */}
          {false && <div className="carrierDialog__card">
            <div className="carrierDialog__sectionTitle">Address</div>

            {/* Address Line 1 — full width so suggestion dropdown has room */}
            <div style={{ position: 'relative', marginBottom: 12 }}>
              <div className="carrierDialog__label">Address Line 1</div>
              <InputText maxLength={100}
                ref={address1Ref}
                value={address1}
                onChange={(e) => handleAddress1Change(e.target.value)}
                placeholder="Start typing to search address..."
                className="carrierDialog__input"
                style={{ width: '100%', margin: 0 }}
              />
              {addressLoading && (
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>
                  <i className="pi pi-spin pi-spinner" style={{ marginRight: 4, fontSize: 11 }} />
                  Loading suggestions...
                </div>
              )}
              {addressSuggestions.length > 0 && (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, border: '1px solid #d1d5db', borderRadius: 6, maxHeight: 200, overflowY: 'auto', background: 'white', position: 'absolute', zIndex: 1000, width: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }}>
                  {addressSuggestions.map((suggestion, index) => (
                    <li
                      key={index}
                      onClick={() => selectAddressSuggestion(suggestion)}
                      style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: 13, color: '#1f2937' }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = '#f0f6ff')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'white')}
                    >
                      <i className="pi pi-map-marker" style={{ fontSize: 11, color: '#A200FF', marginRight: 6 }} />
                      {suggestion.description}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Remaining fields in two aligned columns */}
            <div className="carrierDialog__twoCol">
              <div>
                <div className="carrierDialog__label">Address Line 2</div>
                <InputText maxLength={100}
                  value={address2}
                  onChange={(e) => setAddress2(e.target.value)}
                  placeholder="Please enter address line 2"
                  className="carrierDialog__input"
                />

                <div className="carrierDialog__label">City</div>
                <InputText maxLength={100}
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Please enter city"
                  className="carrierDialog__input"
                />

                <div className="carrierDialog__label">County</div>
                <InputText
                  value={county}
                  onChange={(e) => setCounty(e.target.value)}
                  placeholder="Please enter county"
                  className="carrierDialog__input"
                />
              </div>

              <div>
                <div className="carrierDialog__label">State</div>
                <div className="carrierDialog__flexRow">
                  <Dropdown
                    value={stateValue}
                    options={stateOptions}
                    onChange={(e) => setStateValue(e.value)}
                    placeholder="Select state"
                    style={{ flex: 1 }}
                    emptyMessage="No available options"
                  />
                  {/* <Button
                    type="button"
                    label="Add New State"
                    className="carrierDialog__secondaryBtn"
                  /> */}
                </div>

                <div className="carrierDialog__label">ZIP / Postal Code</div>
                <InputText maxLength={20}
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  placeholder="Please enter ZIP / postal code"
                  className="carrierDialog__input"
                />

                <div className="carrierDialog__label">Finance Co Search Code</div>
                <InputText
                  value={financeCoSearchCode}
                  onChange={(e) => setFinanceCoSearchCode(e.target.value)}
                  placeholder="Please enter finance company search code"
                  className="carrierDialog__input"
                />
              </div>
            </div>

            <div className="carrierDialog__twoCol">
              <div>
                <div className="carrierDialog__label">Lead Sources</div>
                <Dropdown
                  value={leadSource}
                  options={[]}
                  onChange={(e) => setLeadSource(e.value)}
                  placeholder="Type and/or choose"
                  className="carrierDialog__dropdown"
                  emptyMessage="No available options"
                />

                <div className="carrierDialog__label">Description</div>
                <InputTextarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  placeholder="Description"
                  className="carrierDialog__textarea"
                />
              </div>

              <div>
                <div className="carrierDialog__label">Agency</div>
                <Dropdown
                  value={agency}
                  options={[]}
                  onChange={(e) => setAgency(e.value)}
                  placeholder="Select agency"
                  className="carrierDialog__dropdown"
                  emptyMessage="No available options"
                />

                <div className="carrierDialog__label">Primary Location</div>
                <Dropdown
                  value={primaryLocation}
                  options={[]}
                  onChange={(e) => setPrimaryLocation(e.value)}
                  placeholder="Select primary location"
                  className="carrierDialog__dropdownNoMargin"
                  emptyMessage="No available options"
                />
              </div>
            </div>
          </div>}

          {/* Reps */}
          {/* <div className="carrierDialog__card">
            <div className="carrierDialog__sectionTitle">Reps</div>
            {reps.map((rep, idx) => (
              <div
                key={idx}
                className="carrierDialog__repeatRow"
              >
                <div className="carrierDialog__twoCol">
                  <div>
                    <div className="carrierDialog__label">First Name</div>
                    <InputText
                      value={rep.firstName}
                      onChange={(e) => updateRepRow(idx, 'firstName', e.target.value)}
                      placeholder="Please enter first name"
                      className="carrierDialog__input"
                    />

                    <div className="carrierDialog__label">Last Name</div>
                    <InputText
                      value={rep.lastName}
                      onChange={(e) => updateRepRow(idx, 'lastName', e.target.value)}
                      placeholder="Please enter last name"
                      className="carrierDialog__input"
                    />
                  </div>

                  <div>
                    <div className="carrierDialog__label">M.I.</div>
                    <InputText
                      value={rep.middleInitial}
                      onChange={(e) => updateRepRow(idx, 'middleInitial', e.target.value)}
                      placeholder="M.I."
                      maxLength={2}
                      className="carrierDialog__inputSm"
                    />

                    <div className="carrierDialog__label">E-mail</div>
                    <InputText
                      value={rep.email}
                      onChange={(e) => updateRepRow(idx, 'email', e.target.value)}
                      placeholder="Please enter email"
                      className="carrierDialog__input"
                    />
                  </div>
                </div>

                <div className="carrierDialog__twoCol">
                  <div>
                    <div className="carrierDialog__label">Phone</div>
                    <InputText
                      value={rep.phone}
                      onChange={(e) => updateRepRow(idx, 'phone', maskUsPhone(e.target.value))}
                      placeholder="Please enter phone"
                      className="carrierDialog__input"
                    />
                  </div>
                  <div>
                    <div className="carrierDialog__label">Note</div>
                    <InputText
                      value={rep.note}
                      onChange={(e) => updateRepRow(idx, 'note', e.target.value)}
                      placeholder="Please enter note"
                      className="carrierDialog__input"
                    />
                  </div>
                </div>

                <div className="carrierDialog__buttonsRowEnd">
                  <Button
                    type="button"
                    icon="pi pi-times"
                    className="p-button-text p-button-sm"
                    onClick={() => removeRepRow(idx)}
                    disabled={reps.length === 1}
                  />
                </div>
              </div>
            ))}
            <Button
              type="button"
              label="Add Rep"
              icon="pi pi-plus"
              className="carrierDialog__secondaryBtn"
              onClick={addRepRow}
            />
          </div> */}

          {/* Contacts */}
          {/* <div className="carrierDialog__card">
            <div className="carrierDialog__sectionTitle">Contacts</div>
            <div className="carrierDialog__twoCol">
              <div>
                <div className="carrierDialog__label">Phone</div>
                <InputText
                  value={contactPhone}
                  onChange={(e) => setContactPhone(maskUsPhone(e.target.value))}
                  placeholder="Please enter phone"
                  className="carrierDialog__input"
                />

                <div className="carrierDialog__label">Cell Phone</div>
                <InputText
                  value={contactCellPhone}
                  onChange={(e) => setContactCellPhone(maskUsPhone(e.target.value))}
                  placeholder="Please enter cell phone"
                  className="carrierDialog__input"
                />

                <div className="carrierDialog__label">SMS Phone</div>
                <InputText
                  value={smsPhone}
                  onChange={(e) => setSmsPhone(maskUsPhone(e.target.value))}
                  placeholder="Please enter SMS phone"
                  className="carrierDialog__input"
                />

                <div className="carrierDialog__label">Fax</div>
                <InputText maxLength={20}
                  value={fax}
                  onChange={(e) => setFax(maskUsPhone(e.target.value))}
                  placeholder="Please enter fax"
                  className="carrierDialog__input"
                />
              </div>

              <div>
                <div className="carrierDialog__label">Primary Email</div>
                <InputText
                  value={primaryEmail}
                  onChange={(e) => setPrimaryEmail(e.target.value)}
                  placeholder="Please enter primary email"
                  className="carrierDialog__input"
                />

                <div className="carrierDialog__label">Secondary Email</div>
                <InputText
                  value={secondaryEmail}
                  onChange={(e) => setSecondaryEmail(e.target.value)}
                  placeholder="Please enter secondary email"
                  className="carrierDialog__input"
                />

                <div className="carrierDialog__label">Parent</div>
                <div className="carrierDialog__flexRow">
                  <Dropdown
                    value={parent}
                    options={[]}
                    onChange={(e) => setParent(e.value)}
                    placeholder="Type and/or choose"
                    style={{ flex: 1 }}
                    emptyMessage="No available options"
                  />
                  <Button
                      type="button"
                      label="Add New Parent"
                      className="carrierDialog__secondaryBtn"
                  />
                </div>

                <div className="carrierDialog__label">Other Email</div>
                <InputText
                  value={otherEmail}
                  onChange={(e) => setOtherEmail(e.target.value)}
                  placeholder="Please enter other email"
                  className="carrierDialog__input"
                />
              </div>
            </div>

            <div className="carrierDialog__twoCol">
              <div>
                <div className="carrierDialog__label">Main Website</div>
                <InputText
                  value={mainWebsite}
                  onChange={(e) => setMainWebsite(e.target.value)}
                  placeholder="Please enter main website URL"
                  className="carrierDialog__input"
                />

                <div className="carrierDialog__label">Agent Portal</div>
                <InputText
                  value={agentPortal}
                  onChange={(e) => setAgentPortal(e.target.value)}
                  placeholder="Please enter agent portal URL"
                  className="carrierDialog__input"
                />
              </div>

              <div>
                <div className="carrierDialog__label">Username</div>
                <InputText
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Please enter username"
                  className="carrierDialog__input"
                />

                <div className="carrierDialog__label">Password</div>
                <InputText
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  type="password"
                  className="carrierDialog__input"
                />

                <div className="carrierDialog__label">Agency ID</div>
                <InputText
                  value={agencyId}
                  onChange={(e) => setAgencyId(e.target.value)}
                  placeholder="Please enter agency ID"
                  className="carrierDialog__input"
                />
              </div>
            </div>
          </div> */}

          {/* Business / Payment Information */}
          {/* <div className="carrierDialog__card">
            <div className="carrierDialog__sectionTitle">Business / Payment Information</div>
            <div className="carrierDialog__twoCol">
              <div>
                <div className="carrierDialog__label">Remit By</div>
                <InputText
                  value={remitBy}
                  onChange={(e) => setRemitBy(e.target.value)}
                  placeholder="Please enter remit by"
                  className="carrierDialog__input"
                />

                <div className="carrierDialog__label">Gross Commission Due Within</div>
                <InputText
                  value={grossCommissionDueWithin}
                  onChange={(e) => setGrossCommissionDueWithin(e.target.value)}
                  placeholder="Please enter number of days"
                  className="carrierDialog__input"
                />

                <div className="carrierDialog__label">Remaining Payments</div>
                <InputText
                  value={remainingPayments}
                  onChange={(e) => setRemainingPayments(e.target.value)}
                  placeholder="Please enter remaining payments"
                  className="carrierDialog__input"
                />
              </div>

              <div>
                <div className="carrierDialog__label">Minimum Down Payment</div>
                <div className="carrierDialog__flexRow">
                  <InputText
                    value={minimumDownPayment}
                    onChange={(e) => setMinimumDownPayment(e.target.value)}
                    placeholder="Value"
                    style={{ flex: 1 }}
                  />
                  <Dropdown
                    value={minimumDownPaymentUnit}
                    options={percentAmountOptions}
                    onChange={(e) => setMinimumDownPaymentUnit(e.value)}
                    className="carrierDialog__dropdownSm"
                    emptyMessage="No available options"
                  />
                </div>

                <div className="carrierDialog__label">Interest Rate</div>
                <div className="carrierDialog__flexRow">
                  <InputText
                    value={interestRate}
                    onChange={(e) => setInterestRate(e.target.value)}
                    placeholder="Rate"
                    style={{ flex: 1 }}
                  />
                  <Dropdown
                    value={interestRateType}
                    options={interestTypeOptions}
                    onChange={(e) => setInterestRateType(e.value)}
                    className="carrierDialog__dropdownMd"
                    emptyMessage="No available options"
                  />
                </div>

                <div className="carrierDialog__label">Minimum Earned</div>
                <div className="carrierDialog__flexRow">
                  <InputText
                    value={minimumEarned}
                    onChange={(e) => setMinimumEarned(e.target.value)}
                    placeholder="Value"
                    style={{ flex: 1 }}
                  />
                  <Dropdown
                    value={minimumEarnedUnit}
                    options={percentAmountOptions}
                    onChange={(e) => setMinimumEarnedUnit(e.value)}
                    className="carrierDialog__dropdownSm"
                    emptyMessage="No available options"
                  />
                </div>
              </div>
            </div>

            <div className="carrierDialog__twoCol">
              <div>
                <div className="carrierDialog__label">A.M. Best Rating (BCR)</div>
                <Dropdown
                  value={amBestBcr}
                  options={yesNoOptions}
                  onChange={(e) => setAmBestBcr(e.value)}
                  placeholder="Select BCR"
                  className="carrierDialog__dropdown"
                  emptyMessage="No available options"
                />
              </div>
              <div>
                <div className="carrierDialog__label">A.M. Best Rating (FSC)</div>
                <Dropdown
                  value={amBestFsc}
                  options={yesNoOptions}
                  onChange={(e) => setAmBestFsc(e.value)}
                  placeholder="Select FSC"
                  className="carrierDialog__dropdown"
                  emptyMessage="No available options"
                />

                <div className="carrierDialog__label">Premium Sent</div>
                <Dropdown
                  value={premiumSent}
                  options={yesNoOptions}
                  onChange={(e) => setPremiumSent(e.value)}
                  placeholder="Premium sent"
                  className="carrierDialog__dropdownNoMargin"
                  emptyMessage="No available options"
                />
              </div>
            </div>
          </div> */}

          {/* Lines of Businesses */}
          {/* <div className="carrierDialog__card">
            <div className="carrierDialog__sectionTitle">Lines of Businesses</div>
            {linesOfBusiness.map((row, idx) => (
              <div
                key={idx}
                className="carrierDialog__gridThreeCol"
              >
                <Dropdown
                  value={row.line}
                  options={[]}
                  onChange={(e) => updateLineOfBusinessRow(idx, e.value)}
                  placeholder="Type and/or choose"
                  style={{ flex: 1 }}
                  emptyMessage="No available options"
                />
                <div className="carrierDialog__iconBtns">
                  <Button
                    type="button"
                    icon="pi pi-plus"
                    className="carrierDialog__secondaryBtn p-button-sm"
                    onClick={addLineOfBusinessRow}
                  />
                  <Button
                    type="button"
                    icon="pi pi-times"
                    className="p-button-text p-button-sm"
                    onClick={() => removeLineOfBusinessRow(idx)}
                    disabled={linesOfBusiness.length === 1}
                  />
                </div>
              </div>
            ))}
          </div> */}

          {/* Custom Fields */}
          {/* <div className="carrierDialog__card">
            <div className="carrierDialog__sectionTitle">Custom Fields</div>
            {customFields.map((row, idx) => (
              <div
                key={idx}
                className="carrierDialog__gridThreeCol"
              >
                <InputText
                  value={row.text}
                  onChange={(e) => updateCustomFieldRow(idx, 'text', e.target.value)}
                  placeholder="Please enter text"
                />
                <InputText
                  value={row.value}
                  onChange={(e) => updateCustomFieldRow(idx, 'value', e.target.value)}
                  placeholder="Please enter value"
                />
                <div className="carrierDialog__iconBtns">
                  <Button
                    type="button"
                    icon="pi pi-plus"
                    className="carrierDialog__secondaryBtn p-button-sm"
                    onClick={addCustomFieldRow}
                  />
                  <Button
                    type="button"
                    icon="pi pi-times"
                    className="p-button-text p-button-sm"
                    onClick={() => setCustomFields(customFields.length === 1 ? customFields : customFields.filter((_, i) => i !== idx))}
                    disabled={customFields.length === 1}
                  />
                </div>
              </div>
            ))}
          </div> */}

          {/* Agent Carrier Code */}
          {/* <div className="carrierDialog__card">
            <div className="carrierDialog__sectionTitle">Agent Carrier Code</div>
            {agentCarrierCodes.map((row, idx) => (
              <div
                key={idx}
                className="carrierDialog__gridThreeCol"
              >
                <InputText
                  value={row.code}
                  onChange={(e) => updateAgentCarrierCodeRow(idx, 'code', e.target.value)}
                  placeholder="Please enter code"
                />
                <Dropdown
                  value={row.agent}
                  options={[]}
                  onChange={(e) => updateAgentCarrierCodeRow(idx, 'agent', e.value)}
                  placeholder="Type and/or choose agent"
                  emptyMessage="No available options"
                />
                <div className="carrierDialog__iconBtns">
                  <Button
                      type="button"
                      icon="pi pi-plus"
                      className="carrierDialog__secondaryBtn p-button-sm"
                      onClick={addAgentCarrierCodeRow}
                  />
                  <Button
                    type="button"
                    icon="pi pi-times"
                    className="p-button-text p-button-sm"
                    onClick={() => removeAgentCarrierCodeRow(idx)}
                    disabled={agentCarrierCodes.length === 1}
                  />
                </div>
              </div>
            ))}
          </div> */}

          {/* Footer Buttons */}
          <div className="carrierDialog__footer">
            {saveError && (
              <span style={{ fontSize: 12, color: '#dc2626', marginRight: 'auto' }}>
                <i className="pi pi-exclamation-circle" style={{ marginRight: 4 }} />
                {saveError}
              </span>
            )}
            <Button
              type="button"
              label="Cancel"
              className="p-button-text carrierDialog__cancelBtn"
              onClick={onClose}
              disabled={saving}
            />
            <Button
              type="button"
              label={saving ? 'Saving...' : 'Save'}
              icon={saving ? 'pi pi-spin pi-spinner' : undefined}
              className="p-button-primary carrierDialog__saveBtn"
              onClick={handleSave}
              disabled={saving}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateNewCarriers;
